import { ComponentType, Overlay, OverlayPositionBuilder, OverlayRef, ConnectedPosition } from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { 
  Directive, 
  ElementRef, 
  HostListener, 
  Input, 
  OnInit, 
  OnDestroy, 
  TemplateRef, 
  ViewContainerRef,
  Renderer2,
  NgZone
} from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';

export interface TooltipPosition {
  originX: 'start' | 'center' | 'end';
  originY: 'top' | 'center' | 'bottom';
  overlayX: 'start' | 'center' | 'end';
  overlayY: 'top' | 'center' | 'bottom';
  offsetX?: number;
  offsetY?: number;
}

export interface TooltipConfig {
  positions?: TooltipPosition[];
  panelClass?: string | string[];
  showDelay?: number;
  hideDelay?: number;
  disabled?: boolean;
  useSmartPositioning?: boolean;
  minSpaceRequired?: number;
  interactive?: boolean; // NEW: Enable interactive tooltips
  interactiveDelay?: number; // NEW: Extra delay when moving to tooltip
  trigger?: 'hover' | 'click'; // NEW: 'click' is meant for touch devices where hover doesn't apply
}

interface ViewportSpace {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface PositionScore {
  position: TooltipPosition;
  score: number;
  availableSpace: number;
}

@Directive({
  selector: '[customTooltip]'
})
export class CustomTooltipDirective implements OnInit, OnDestroy {
  @Input('customTooltip') tooltipContent: TemplateRef<any> | ComponentType<any>;
  @Input() tooltipConfig: TooltipConfig = {};
  @Input() tooltipDisabled: boolean = false;
  @Input() customTooltipContext: any = {};
  
  private _overlayRef: OverlayRef | null = null;
  private _showTimeoutId: number | null = null;
  private _hideTimeoutId: number | null = null;
  private _isTooltipVisible: boolean = false;
  private _scrollSubscription: Subscription | null = null;
  private _resizeSubscription: Subscription | null = null;
  
  // NEW: Interactive tooltip support
  private _mouseInsideTooltip: boolean = false;
  private _mouseInsideTrigger: boolean = false;
  private _tooltipMouseSubscriptions: Subscription[] = [];

  // Default configuration
  private readonly defaultConfig: Required<TooltipConfig> = {
    positions: [],
    panelClass: ['custom-tooltip', 'animate__animated', 'animate__zoomIn', 'animate__faster'],
    showDelay: 200,
    hideDelay: 100,
    disabled: false,
    useSmartPositioning: true,
    minSpaceRequired: 100,
    interactive: true, // NEW: Enable by default
    interactiveDelay: 300, // NEW: Extra delay when moving to tooltip
    trigger: 'hover' // NEW: default keeps existing hover behavior
  };

  private readonly allPossiblePositions: TooltipPosition[] = [
    // Primary positions - centered
    { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetX: 0, offsetY: 8 },
    { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetX: 0, offsetY: -8 },
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8, offsetY: 0 },
    { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8, offsetY: 0 },
    
    // Diagonal positions - corners
    { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetX: 8, offsetY: 8 },
    { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetX: -8, offsetY: 8 },
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetX: 8, offsetY: -8 },
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetX: -8, offsetY: -8 },
    
    // Offset positions
    { originX: 'center', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetX: -20, offsetY: 8 },
    { originX: 'center', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetX: 20, offsetY: 8 },
    { originX: 'center', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetX: -20, offsetY: -8 },
    { originX: 'center', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetX: 20, offsetY: -8 },
    
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'top', offsetX: 8, offsetY: -20 },
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'bottom', offsetX: 8, offsetY: 20 },
    { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'top', offsetX: -8, offsetY: -20 },
    { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'bottom', offsetX: -8, offsetY: 20 }
  ];

  constructor(
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef,
    private viewContainerRef: ViewContainerRef,
    private renderer: Renderer2,
    private scrollDispatcher: ScrollDispatcher,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    if (!this.tooltipContent) {
      console.warn('[CustomTooltip] No content provided for tooltip. Directive will be disabled.');
      return;
    }

    this.setupAccessibility();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  @HostListener('mouseenter')
  @HostListener('focus')
  onShow(): void {
    if (this.getConfig().trigger === 'click') {
      return;
    }
    this._mouseInsideTrigger = true;
    if (this.shouldShowTooltip()) {
      this.scheduleShow();
    }
  }

  @HostListener('mouseleave')
  @HostListener('blur')
  onHide(): void {
    if (this.getConfig().trigger === 'click') {
      return;
    }
    this._mouseInsideTrigger = false;

    const config = this.getConfig();
    if (config.interactive) {
      // For interactive tooltips, add a delay to allow moving to tooltip
      this.scheduleInteractiveHide();
    } else {
      // For non-interactive tooltips, hide immediately
      this.scheduleHide();
    }
  }

  // NEW: Tap-to-toggle support for touch devices (trigger: 'click')
  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (this.getConfig().trigger !== 'click' || !this.shouldShowTooltip()) {
      return;
    }
    event.stopPropagation();
    this.clearTimeouts();
    if (this._isTooltipVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  @HostListener('keydown.escape')
  onEscapeKey(): void {
    if (this._isTooltipVisible) {
      this.hide();
    }
  }

  private shouldShowTooltip(): boolean {
    return !!(this.tooltipContent && !this.tooltipDisabled && !this.getConfig().disabled);
  }

  private getConfig(): Required<TooltipConfig> {
    const config = {
      ...this.defaultConfig,
      ...this.tooltipConfig
    };
    
    if (config.useSmartPositioning && (!this.tooltipConfig.positions || this.tooltipConfig.positions.length === 0)) {
      config.positions = this.calculateSmartPositions();
    }
    
    return config;
  }

  private scheduleShow(): void {
    this.clearTimeouts();
    
    const config = this.getConfig();
    this._showTimeoutId = window.setTimeout(() => {
      this.show();
    }, config.showDelay);
  }

  private scheduleHide(): void {
    this.clearTimeouts();
    
    const config = this.getConfig();
    this._hideTimeoutId = window.setTimeout(() => {
      this.hide();
    }, config.hideDelay);
  }

  // NEW: Special hide logic for interactive tooltips
  private scheduleInteractiveHide(): void {
    this.clearTimeouts();
    
    const config = this.getConfig();
    const delay = config.interactiveDelay;
    
    this._hideTimeoutId = window.setTimeout(() => {
      // Only hide if mouse is not inside tooltip and not inside trigger
      if (!this._mouseInsideTooltip && !this._mouseInsideTrigger) {
        this.hide();
      }
    }, delay);
  }

  private show(): void {
    if (this._isTooltipVisible || !this.shouldShowTooltip()) {
      return;
    }

    this.disposeOverlay();
    this.createOverlay();
    
    if (this._overlayRef && !this._overlayRef.hasAttached()) {
      const portal = this.createPortal();
      this._overlayRef.attach(portal);
      this._isTooltipVisible = true;
      
      this.setupDynamicRepositioning();
      this.setupTooltipInteractivity(); // NEW
      
      this.renderer.setAttribute(this.elementRef.nativeElement, 'aria-describedby', this.getTooltipId());
    }
  }

  private hide(): void {
    if (!this._isTooltipVisible) {
      return;
    }

    this.cleanupDynamicRepositioning();
    this.cleanupTooltipInteractivity(); // NEW

    if (this._overlayRef && this._overlayRef.hasAttached()) {
      this._overlayRef.detach();
      this._isTooltipVisible = false;
      this._mouseInsideTooltip = false;
      
      this.renderer.removeAttribute(this.elementRef.nativeElement, 'aria-describedby');
    }
  }

  // NEW: Setup mouse event listeners for tooltip interactivity
  private setupTooltipInteractivity(): void {
    const config = this.getConfig();
    if (!config.interactive || !this._overlayRef) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const tooltipElement = this._overlayRef!.overlayElement;
      
      // Mouse enter tooltip
      const mouseEnterSub = fromEvent(tooltipElement, 'mouseenter').subscribe(() => {
        this.ngZone.run(() => {
          this._mouseInsideTooltip = true;
          this.clearTimeouts(); // Cancel any pending hide
        });
      });

      // Mouse leave tooltip
      const mouseLeaveSub = fromEvent(tooltipElement, 'mouseleave').subscribe(() => {
        this.ngZone.run(() => {
          this._mouseInsideTooltip = false;
          // Check if we should hide (trigger is also not hovered)
          if (!this._mouseInsideTrigger) {
            this.scheduleHide();
          }
        });
      });

      this._tooltipMouseSubscriptions.push(mouseEnterSub, mouseLeaveSub);
    });
  }

  // NEW: Cleanup tooltip interactivity
  private cleanupTooltipInteractivity(): void {
    this._tooltipMouseSubscriptions.forEach(sub => sub.unsubscribe());
    this._tooltipMouseSubscriptions = [];
  }

  private createOverlay(): void {
    if (this._overlayRef) {
      return;
    }

    const config = this.getConfig();
    const positions = this.mapToConnectedPositions(config.positions);
    
    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions(positions)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withGrowAfterOpen(false)
      .withViewportMargin(8);

    this._overlayRef = this.overlay.create({
      positionStrategy,
      // NEW: Don't close on scroll for interactive tooltips
      scrollStrategy: config.interactive 
        ? this.overlay.scrollStrategies.reposition() 
        : this.overlay.scrollStrategies.close(),
      panelClass: config.panelClass,
      hasBackdrop: false,
      disposeOnNavigation: true,
      width: 'max-content',
      height: 'max-content',
      minWidth: 0,
      minHeight: 0,
      maxWidth: '90vw',
      maxHeight: '90vh'
    });

    // Handle outside clicks only for interactive tooltips
    if (config.interactive) {
      this._overlayRef.outsidePointerEvents().subscribe((event) => {
        // Only hide if clicked outside both trigger and tooltip
        if (!this.elementRef.nativeElement.contains(event.target)) {
          this.hide();
        }
      });
    } else {
      this._overlayRef.outsidePointerEvents().subscribe(() => {
        this.hide();
      });
    }
  }

  private createPortal(): TemplatePortal | ComponentPortal<any> {
    if (this.tooltipContent instanceof TemplateRef) {
      return new TemplatePortal(this.tooltipContent, this.viewContainerRef, {
        $implicit: this.customTooltipContext,
        ...this.customTooltipContext
      });
    } else {
      return new ComponentPortal(this.tooltipContent, this.viewContainerRef);
    }
  }

  private mapToConnectedPositions(positions: TooltipPosition[]): ConnectedPosition[] {
    return positions.map(pos => ({
      originX: pos.originX,
      originY: pos.originY,
      overlayX: pos.overlayX,
      overlayY: pos.overlayY,
      offsetX: pos.offsetX || 0,
      offsetY: pos.offsetY || 0,
    }));
  }

  private setupAccessibility(): void {
    const element = this.elementRef.nativeElement;
    
    if (!element.hasAttribute('tabindex') && !this.isNaturallyFocusable(element)) {
      this.renderer.setAttribute(element, 'tabindex', '0');
    }

    if (!element.hasAttribute('role')) {
      this.renderer.setAttribute(element, 'role', 'button');
    }
  }

  private isNaturallyFocusable(element: HTMLElement): boolean {
    const focusableElements = [
      'input', 'select', 'textarea', 'button', 'a', 'area', 'summary'
    ];
    
    return focusableElements.includes(element.tagName.toLowerCase()) ||
           element.hasAttribute('href') ||
           element.hasAttribute('contenteditable');
  }

  private getTooltipId(): string {
    return `tooltip-${Math.random().toString(36).substr(2, 9)}`;
  }

  private clearTimeouts(): void {
    if (this._showTimeoutId) {
      clearTimeout(this._showTimeoutId);
      this._showTimeoutId = null;
    }

    if (this._hideTimeoutId) {
      clearTimeout(this._hideTimeoutId);
      this._hideTimeoutId = null;
    }
  }

  private cleanup(): void {
    this.clearTimeouts();
    this.cleanupDynamicRepositioning();
    this.cleanupTooltipInteractivity();
    this.disposeOverlay();
    this._isTooltipVisible = false;
    this._mouseInsideTooltip = false;
    this._mouseInsideTrigger = false;
  }

  private disposeOverlay(): void {
    if (this._overlayRef) {
      if (this._overlayRef.hasAttached()) {
        this._overlayRef.detach();
      }
      this._overlayRef.dispose();
      this._overlayRef = null;
    }
  }

  private setupDynamicRepositioning(): void {
    if (!this._overlayRef) return;

    this.ngZone.runOutsideAngular(() => {
      this._scrollSubscription = this.scrollDispatcher.scrolled()
        .pipe(throttleTime(16))
        .subscribe(() => {
          this.ngZone.run(() => {
            this.updateTooltipPosition();
          });
        });

      this._resizeSubscription = fromEvent(window, 'resize')
        .pipe(throttleTime(100))
        .subscribe(() => {
          this.ngZone.run(() => {
            this.updateTooltipPosition();
          });
        });
    });
  }

  private cleanupDynamicRepositioning(): void {
    if (this._scrollSubscription) {
      this._scrollSubscription.unsubscribe();
      this._scrollSubscription = null;
    }
    
    if (this._resizeSubscription) {
      this._resizeSubscription.unsubscribe();
      this._resizeSubscription = null;
    }
  }

  private updateTooltipPosition(): void {
    if (!this._overlayRef || !this._overlayRef.hasAttached() || !this._isTooltipVisible) {
      return;
    }

    const elementRect = this.elementRef.nativeElement.getBoundingClientRect();
    const isElementVisible = elementRect.top >= 0 && 
                           elementRect.left >= 0 && 
                           elementRect.bottom <= window.innerHeight && 
                           elementRect.right <= window.innerWidth;

    if (!isElementVisible) {
      this.hide();
      return;
    }

    const config = this.getConfig();
    if (config.useSmartPositioning) {
      const newPositions = this.calculateSmartPositions();
      const mappedPositions = this.mapToConnectedPositions(newPositions);
      
      const positionStrategy = this._overlayRef.getConfig().positionStrategy as any;
      if (positionStrategy && positionStrategy.withPositions) {
        positionStrategy.withPositions(mappedPositions);
        this._overlayRef.updatePositionStrategy(positionStrategy);
      }
    }
  }

  // Smart positioning methods (same as before)
  private calculateSmartPositions(): TooltipPosition[] {
    const elementRect = this.elementRef.nativeElement.getBoundingClientRect();
    const viewportSpace = this.getViewportSpace(elementRect);
    const config = { ...this.defaultConfig, ...this.tooltipConfig };
    
    const scoredPositions = this.allPossiblePositions.map(position => {
      return this.scorePosition(position, viewportSpace, config.minSpaceRequired);
    }).sort((a, b) => b.score - a.score);
    
    const viablePositions = scoredPositions
      .filter(pos => pos.score > 0 && pos.availableSpace >= config.minSpaceRequired)
      .slice(0, 6);
    
    if (viablePositions.length === 0) {
      return [
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetX: 0, offsetY: 8 },
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetX: 0, offsetY: -8 },
        { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8, offsetY: 0 },
        { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8, offsetY: 0 }
      ];
    }
    
    return viablePositions.map(pos => pos.position);
  }

  private getViewportSpace(elementRect: DOMRect): ViewportSpace {
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const padding = 10;
    
    return {
      top: elementRect.top - padding,
      bottom: viewport.height - elementRect.bottom - padding,
      left: elementRect.left - padding,
      right: viewport.width - elementRect.right - padding
    };
  }

  private scorePosition(position: TooltipPosition, viewportSpace: ViewportSpace, minSpace: number): PositionScore {
    let availableSpace = 0;
    let score = 0;
    
    const isTooltipBelow = position.overlayY === 'top';
    const isTooltipAbove = position.overlayY === 'bottom';
    const isTooltipRight = position.overlayX === 'start';
    const isTooltipLeft = position.overlayX === 'end';
    const isTooltipCenterH = position.overlayX === 'center';
    const isTooltipCenterV = position.overlayY === 'center';
    
    if (isTooltipBelow && isTooltipCenterH) {
      availableSpace = viewportSpace.bottom;
    } else if (isTooltipAbove && isTooltipCenterH) {
      availableSpace = viewportSpace.top;
    } else if (isTooltipRight && isTooltipCenterV) {
      availableSpace = viewportSpace.right;
    } else if (isTooltipLeft && isTooltipCenterV) {
      availableSpace = viewportSpace.left;
    } else if (isTooltipBelow && (isTooltipLeft || isTooltipRight)) {
      const horizontalSpace = isTooltipRight ? viewportSpace.right : viewportSpace.left;
      const verticalSpace = viewportSpace.bottom;
      availableSpace = Math.min(horizontalSpace, verticalSpace);
    } else if (isTooltipAbove && (isTooltipLeft || isTooltipRight)) {
      const horizontalSpace = isTooltipRight ? viewportSpace.right : viewportSpace.left;
      const verticalSpace = viewportSpace.top;
      availableSpace = Math.min(horizontalSpace, verticalSpace);
    } else {
      availableSpace = Math.min(viewportSpace.top, viewportSpace.bottom, viewportSpace.left, viewportSpace.right);
    }
    
    if (availableSpace < minSpace) {
      score = 0;
    } else {
      score = Math.min(availableSpace * 3, 1500);
      
      if (isTooltipBelow && isTooltipCenterH) {
        score += 50;
      } else if (isTooltipAbove && isTooltipCenterH) {
        score += 40;
      } else if ((isTooltipLeft || isTooltipRight) && isTooltipCenterV) {
        score += 30;
      } else if (isTooltipBelow && (isTooltipLeft || isTooltipRight)) {
        score += 25;
      } else if (isTooltipAbove && (isTooltipLeft || isTooltipRight)) {
        score += 20;
      } else {
        score += 10;
      }
      
      if (availableSpace > minSpace * 5) {
        score += 100;
      } else if (availableSpace > minSpace * 3) {
        score += 75;
      } else if (availableSpace > minSpace * 2) {
        score += 50;
      }
      
      const allSpaces = [viewportSpace.top, viewportSpace.bottom, viewportSpace.left, viewportSpace.right];
      const minOtherSpace = Math.min(...allSpaces);
      if (minOtherSpace < minSpace * 0.5) {
        score -= 30;
      }
    }
    
    return { position, score, availableSpace };
  }
}